/**
 * Boxes 11 (channels), 12 (ad accounts) and 13 (top 5 campaigns) (spec §3.3).
 * Small samples fade (R17); box 13 chips names outside the convention and
 * never prints the summary's 0 for an unreported Result (R4); its header stays
 * "Result" and each Result names its metric on a second line, as D3 does
 * (rules-04 E3); under Reach every Awareness campaign carries box 9's badge
 * "นิยามของ workspace: Reach" (box 9's own gate, reachBadgeShown), and the
 * caveat sits under the table (rules-04 C3). An ad channel's
 * row in box 11, and an ad-channel campaign in box 13, open the Channel
 * Deep-dive with the page's link query (the shared filters, D2 spec §4.1, and
 * scope=all in the master's all-workspaces view). Any other row stays text.
 *
 * @module components/overview/MetricTables
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { platformLabel } from '@/utils/platformLabel';
import { isAdChannel } from '@/services/channels/channelTypes';
import type { MetricRow } from '@/services/overview/breakdownTables';
import type { CampaignRow } from '@/services/overview/topCampaigns';
import { DASH, fmtCount, fmtMoney, fmtPct, objectiveText } from '@/services/overview/overviewFormat';
import { reachBadgeShown, type ResultMappingInfo } from '@/services/targets/resultMetric';
import { Chip, LowSample, OverviewTable, ReachBadge, ResultFigure, type OverviewColumn } from './OverviewTable';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/** Underlined on screen; the PDF drops the underline, as D1 printed these names before they were links (M5). */
const LINK_CLASS = 'font-medium text-ink-950 underline underline-offset-2 hover:text-ink-800 print:no-underline';

/** D1 → D2: the channel's Deep-dive with the shared filters, on one campaign when one is given. */
const deepDiveHref = (platform: string, linkSearch: string, campaignId?: string): string =>
  `/channels/${platform}?${linkSearch}${campaignId ? `&campaign=${encodeURIComponent(campaignId)}` : ''}`;

const metricColumns = (t: TFn, money: (r: MetricRow) => string | null): Array<OverviewColumn<MetricRow>> => [
  { id: 'spend', header: t('overview.metric.spend'), numeric: true, cell: (r) => fmtMoney(r.spend, money(r)) },
  { id: 'impressions', header: t('overview.metric.impressions'), numeric: true, cell: (r) => fmtCount(r.impressions) },
  { id: 'clicks', header: t('overview.metric.clicks'), numeric: true, cell: (r) => fmtCount(r.clicks) },
  { id: 'ctr', header: t('overview.metric.ctr'), numeric: true, cell: (r) => fmtPct(r.ctr) },
  { id: 'cpc', header: t('overview.metric.cpc'), numeric: true, cell: (r) => fmtMoney(r.cpc, money(r)) },
  { id: 'cpm', header: t('overview.metric.cpm'), numeric: true, cell: (r) => fmtMoney(r.cpm, money(r)) },
];

export const ChannelTable: React.FC<{ rows: MetricRow[]; currency: string | null; linkSearch: string }> = ({ rows, currency, linkSearch }) => {
  const { t } = useTranslation();
  const channel = (r: MetricRow): React.ReactNode => (isAdChannel(r.platform)
    ? <Link to={deepDiveHref(r.platform, linkSearch)} className={LINK_CLASS}>{platformLabel(r.platform)}</Link>
    : platformLabel(r.platform));
  const columns: Array<OverviewColumn<MetricRow>> = [
    { id: 'channel', header: t('overview.col.channel'), cell: (r) => <>{channel(r)}<LowSample on={r.lowSample} /></> },
    ...metricColumns(t, () => currency),
  ];
  return <OverviewTable columns={columns} rows={rows} rowKey={(r) => r.key} faded={(r) => r.lowSample} />;
};

export const AccountTable: React.FC<{ rows: MetricRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const columns: Array<OverviewColumn<MetricRow>> = [
    { id: 'account', header: t('overview.col.account'), cell: (r) => <>{r.label}<LowSample on={r.lowSample} /></> },
    { id: 'channel', header: t('overview.col.channel'), cell: (r) => platformLabel(r.platform) },
    { id: 'currency', header: t('overview.col.currency'), cell: (r) => r.currency ?? DASH },
    ...metricColumns(t, (r) => r.currency),
  ];
  return <OverviewTable columns={columns} rows={rows} rowKey={(r) => r.key} faded={(r) => r.lowSample} />;
};

export const TopCampaignsTable: React.FC<{
  rows: CampaignRow[]; currency: string | null; cutApplied: boolean; linkSearch: string; mapping: ResultMappingInfo | null;
}> = ({ rows, currency, cutApplied, linkSearch, mapping }) => {
  const { t } = useTranslation();
  // rules-04 C3: box 9's gate badges every Awareness campaign; the caveat goes under the table when a Result counts reach.
  const reach = rows.some((r) => r.metric === 'reach');
  const campaign = (r: CampaignRow): React.ReactNode => (isAdChannel(r.platform)
    ? <Link to={deepDiveHref(r.platform, linkSearch, r.id)} className={LINK_CLASS}>{r.name}</Link>
    : r.name);
  const columns: Array<OverviewColumn<CampaignRow>> = [
    { id: 'campaign', header: t('overview.col.campaign'), cell: (r) => <>{campaign(r)}{r.unclassified && <Chip text={t('looker.coverage.unclassified')} />}</> },
    { id: 'channel', header: t('overview.col.channel'), cell: (r) => platformLabel(r.platform) },
    { id: 'objective', header: t('overview.col.objective'), cell: (r) => <>{objectiveText(r.objective, t)}<ReachBadge on={reachBadgeShown(r.objective, mapping)} /></> },
    { id: 'spend', header: t('overview.metric.spend'), numeric: true, cell: (r) => fmtMoney(r.spend, currency) },
    { id: 'result', header: t('overview.result'), numeric: true, cell: (r) => <ResultFigure row={r} /> },
  ];
  return (
    <div>
      <OverviewTable columns={columns} rows={rows} rowKey={(r) => r.id} />
      {cutApplied && <p className="mt-2 text-xs text-neutral-700"><span className="inline-block rounded-full border border-neutral-400 px-1.5 text-[11px] text-neutral-800">{t('overview.cutChip')}</span></p>}
      {reach && <p className="mt-2 text-xs text-warning-700">{t('looker.resultMapping.caveat')}</p>}
    </div>
  );
};
